import { PrimaryButton } from '@/components/Button';
import PlusIcon from '@/assets/icons/Plus.svg';
import TrashIcon from '@/assets/icons/Trash.svg';
import EditIcon from '@/assets/icons/Edit.svg';
import CopyIcon from '@/assets/icons/Copy.svg';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';
import ExternalIcon from '@/assets/icons/External.svg';
import { useApiKeys } from './hooks/useApiKey';
import { useRequest } from 'ahooks';
import { getSecretKey, SecretDto } from '@/service/apiKey';
import { useClipboard } from 'use-clipboard-copy';
import { toast } from 'sonner';
import { DOCUMENTATION_URL } from '@/config';
import { CardTitle } from '@/components/ui/card';

const ApiKeys = () => {
  const { editApiKey, deleteApiKey, createApiKey } = useApiKeys();
  const { data, refresh, loading } = useRequest(getSecretKey);
  const apiListData = data?.data?.keys || [];
  const showCreateApi = !apiListData?.length;

  const onCreate = async () => {
    await createApiKey();
    refresh();
  };
  const renderApiListOrIntroduce = () => {
    if (showCreateApi) {
      return <CreateApiIntroduce onCreate={onCreate} />;
    } else {
      return (
        <ApiKeysList
          onEdit={async ({ ...props }) => {
            await editApiKey({ ...props });
            refresh();
          }}
          onDelete={async ({ ...props }) => {
            await deleteApiKey({ ...props });
            refresh();
          }}
          apiListData={apiListData}
        />
      );
    }
  };
  return (
    <div>
      <Header onCreate={onCreate} />
      <Line />
      <Introduce />
      {!loading && renderApiListOrIntroduce()}
    </div>
  );
};

const Header = ({ onCreate }: { onCreate: () => Promise<any> }) => {
  return (
    <div className="p-6 flex justify-between items-center">
      <CardTitle>API Keys</CardTitle>
      <PrimaryButton onClick={onCreate}>
        <PlusIcon fillColor="#fff" />
        Create New API Key
      </PrimaryButton>
    </div>
  );
};

const Introduce = () => {
  return (
    <div className="p-8">
      <div className="max-w-[640px] text-[#48484b] leading-[20px] text-[16px] tracking-[-0.32px]">
        <div className="mb-5">Create and manage API keys here</div>
      </div>
    </div>
  );
};

const ApiKeysList = ({
  apiListData,
  onEdit,
  onDelete,
}: {
  apiListData: SecretDto[];
  onEdit: ({ initName, id }: { initName?: string; id: string }) => Promise<any>;
  onDelete: ({ id }: { id: string }) => Promise<any>;
}) => {
  const clipboard = useClipboard();
  return (
    <div className="pt-8">
      <Table>
        <TableHeader>
          <TableRow className="font-pressura-mono text-[#747479] text-[12px] ">
            <TableHead className="pl-8">NAME</TableHead>
            <TableHead>SECRET KEY</TableHead>
            <TableHead>CREATED</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {apiListData?.map(({ created_at, name, secret_key, id }) => {
            return (
              <TableRow>
                <TableCell>{name}</TableCell>
                <TableCell className="flex gap-2 items-center cursor-pointer ">
                  {maskSecretKey(secret_key)}{' '}
                  <CopyIcon
                    onClick={() => {
                      clipboard.copy(secret_key);
                      toast.info('Copy success');
                    }}
                    fillColor="#71717a"
                  />
                </TableCell>
                <TableCell className="whitespace-nowrap">{dayjs(created_at).format('DD MMM YYYY')}</TableCell>
                <TableCell className="flex gap-[12px] items-center  cursor-pointer">
                  <EditIcon fillColor="#71717a" onClick={() => onEdit({ initName: name, id })} />
                  <TrashIcon fillColor="#bc3229" onClick={() => onDelete({ id })} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
function maskSecretKey(secretKey: string): string {
  const visiblePart = secretKey.slice(0, 3);
  const maskedPart = '•'.repeat(secretKey.length - 3);

  return visiblePart + maskedPart;
}

const Line = () => {
  return <div className="border-b border-jen-border-primary w-full"></div>;
};

export const CreateApiIntroduce = ({ onCreate }: { onCreate: () => Promise<any> }) => {
  return (
    <div className="p-4 lg:p-8 lg:pt-0">
      <MatrixBox className="p-6">
        <div className="text-jen-content-primary text-[20px] leading-[24px] tracking-[-0.6px]">Create an API Key to access the Jen API</div>
        <div className="max-w-[640px] text-jen-content-secondary leading-[140%] tracking-[-0.32px] mt-[16px]">
          Keep your keys secure as they grant access to your account. Treat your keys like passwords - do not share or expose them publicly.
        </div>
        <PrimaryButton
          className="my-8"
          onClick={async () => {
            await onCreate();
          }}
        >
          <PlusIcon fillColor="#fff" />
          Create New API Key
        </PrimaryButton>
        <Line />
        <div className="flex justify-between items-center text-jen-content-secondary mt-6 cursor-pointer">
          <a className=" underline" href={DOCUMENTATION_URL}>
            Read the API Documentation{' '}
          </a>
          <ExternalIcon fillColor="currentColor" />
        </div>
      </MatrixBox>
    </div>
  );
};

const MatrixBox: React.FC<{ className?: string; children: React.ReactNode }> = ({ children, className }) => {
  return (
    <div className={cn('h-full  bg-jen-background-secondary rounded-lg p-4 border border-jen-border-primary', className)}>{children}</div>
  );
};

export default ApiKeys;
